import * as React from 'react';
import { Field, ErrorMessage } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faSignInAlt } from '@fortawesome/free-solid-svg-icons';

interface SortableItemProps {
  index: number;
  student: { _id?: string; name: string; surname: string; order?: number };
  values: any;
  setFieldValue: (field: string, value: any) => void;
  setHasChanges: (hasChanges: boolean) => void;
  onDragStart: (index: number) => void;
  onDrop: (dropIndex: number, dragIndex: number) => void;
  onSplit?: (index: number) => void;
  fontName: string;
}

const fontMapping: Record<string, string> = {
  ARIAL: 'Arial',
  GILL_SANS: 'Gill Sans',
  HELVETICA: 'Helvetica',
  TIMES_NEW_ROMAN: 'Times New Roman',
};

const SortableItem: React.FC<SortableItemProps> = ({
  index,
  student,
  values,
  setFieldValue,
  setHasChanges,
  onDragStart,
  onDrop,
  onSplit,
  fontName,
}) => {
  const handleRemove = () => {
    const newStudents = [...values.groupStudents];
    newStudents.splice(index, 1);
    setFieldValue('groupStudents', newStudents);
    setHasChanges(true);
  };

  return (
    <div
      draggable
      onDragStart={e => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index.toString());
        onDragStart(index);
      }}
      onDragOver={e => {
        e.preventDefault();
      }}
      onDrop={e => {
        e.preventDefault();
        const dragIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
        onDrop(index, dragIdx);
      }}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        flex: '0 0 250px',
        padding: '4px',
        boxSizing: 'border-box',
        borderRadius: '4px',
        border: '1px solid #ccc',
        backgroundColor: 'white',
        gap: '0.5rem',
      }}
    >
      <div style={{ cursor: 'grab', marginRight: '0.5rem' }}>
        <FontAwesomeIcon
          icon={faBars}
          style={{ color: '#27ae60' }}
          title="Drag to reorder students"
          aria-label="Drag to reorder"
        />
      </div>

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '4px',
        }}
      >
        <Field
          name={`groupStudents[${index}].name`}
          placeholder="Name"
          className="custom-form-control"
          style={{
            fontSize: '0.8rem',
            padding: '0.25rem',
            fontFamily: fontMapping[fontName],
            textAlign: 'left',
            width: '100%',
            boxSizing: 'border-box',
          }}
        />
        <Field
          name={`groupStudents[${index}].surname`}
          placeholder="Surname"
          className="custom-form-control"
          style={{
            fontSize: '0.8rem',
            padding: '0.25rem',
            fontFamily: fontMapping[fontName],
            textAlign: 'left',
            width: '100%',
            boxSizing: 'border-box',
          }}
        />
        <ErrorMessage
          name={`groupStudents[${index}].surname`}
          render={msg => (
            <div className="alert alert-danger mt-1" style={{ fontSize: '0.7rem' }}>
              {msg}
            </div>
          )}
        />
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <button
          type="button"
          className="btn btn-danger btn-sm"
          onClick={handleRemove}
          title="Delete this student"
          aria-label="Delete student"
        >
          &times;
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => onSplit(index)}
          title="Add a new row after this student"
          aria-label="Split row"
        >
          <FontAwesomeIcon icon={faSignInAlt} />
        </button>
      </div>
    </div>
  );
};

export default SortableItem;
